import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router';
import { firebaseAuth } from '@/lib/firebase';
import { loginConnect } from '@/features/auth/api';
import { useAuthStore } from '@/features/auth/store';
import { useWorkspaceStore } from '@/features/workspaces';
export function useCurrentUser() {
    return useAuthStore((s) => s.user);
}
// Provider-agnostic login — accepts any Firebase sign-in function.
// Example usage: useLogin() → mutate(() => signInWithPopup(firebaseAuth, new GoogleAuthProvider()))
export function useLogin() {
    const navigate = useNavigate();
    const setAuth = useAuthStore((s) => s.setAuth);
    return useMutation({
        mutationFn: async (signInFn) => {
            const credential = await signInFn();
            const idToken = await credential.user.getIdToken();
            const response = await loginConnect(idToken);
            return { response };
        },
        onSuccess: ({ response }) => {
            const { accessToken, refreshToken, user } = response.data;
            setAuth({ accessToken, refreshToken }, {
                // API returns _id and fullname — normalize to uid and displayName
                uid: user._id,
                email: user.email,
                displayName: user.fullname,
                avatar: user.avatar,
                username: user.username,
                role: user.role,
                membership: user.membership,
            });
            void navigate('/workspaces');
        },
    });
}
export function useLogout() {
    const navigate = useNavigate();
    const clearAuth = useAuthStore((s) => s.clearAuth);
    const clearWorkspace = useWorkspaceStore((s) => s.clearWorkspace);
    const queryClient = useQueryClient();
    return useCallback(async () => {
        await signOut(firebaseAuth);
        clearAuth();
        clearWorkspace();
        queryClient.clear();
        void navigate('/login');
    }, [navigate, clearAuth, clearWorkspace, queryClient]);
}
